/** @jsxImportSource @emotion/react */
import React, { ReactNode, useEffect } from "react";

import { css } from "@emotion/react";

import CircularProgress from "@mui/material/CircularProgress";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useMainBannerStore } from "@/stores/useMainBannerStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { IntegrationsRow } from "../../Pages/Integrations/types";
import { useAuth } from "../../context/Auth/AuthProvider";
import { USER_PERMISSIONS } from "../../roles/types/enums";
import { ROUTES, useMediaQueries } from "../../shared";
import { useReleaseFeatureToggle } from "../../shared/featureToggles/hooks";
import { getEnvironment } from "../../shared/functions/functions";
import useGeneralHeights from "../../shared/hooks/useGeneralHeights";
import TableLoader from "../Loaders/TableLoader";

import DesktopTable from "./components/DesktopTable/DesktopTable";
import MobileTable from "./components/MobileTable/MobileTable";
import TableEmpty from "./components/TableEmpty/TableEmpty";
import TableFooter from "./components/TableFooter/TableFooter";
import TableToolbar from "./components/TableToolbar/TableToolbar";
import { getSelectableRows } from "./functions";
import { useTableStyles } from "./styles/styles";

interface ITableProps {
  hasDetails: boolean;
  route: ROUTES;
  tabFilters?: ReactNode;
  hasTabFilters?: boolean;
  hasTableView?: boolean;
  hasFooter?: boolean;
  hasSearch?: boolean;
  hideFilters?: boolean;
  tableRowSwitch?: (rowData: IntegrationsRow) => Promise<void>;
}

const Table = ({
  hasDetails,
  route,
  tabFilters,
  hasTabFilters,
  hasTableView,
  hasFooter = true,
  hasSearch = true,
  hideFilters,
  tableRowSwitch
}: ITableProps) => {
  const { user, getAccessStatus } = useAuth();
  const { toMd, toLg, toSm } = useMediaQueries();
  const { isProductionEnv } = getEnvironment();
  const { isReleaseFeatureEnabledForUser } = useReleaseFeatureToggle();

  const {
    loading,
    initialLoading,
    data: tableRowData,
    setParams,
    clearSelection,
    setSelectableRows
  } = useTableStoreByRoute(route);

  const isDetailsResetNeeded = useDetailsStore(
    state => state.resetDetailsSection
  );

  const mainBannerHeight = useMainBannerStore(state => state.height);

  const {
    COMPANIES_SX_MOBILE_TABLE_HEIGHT,
    DESKTOP_TABLE_HEIGHT,
    MOBILE_TABLE_HEIGHT,
    MOBILE_TABLE_HEIGHT_WITHOUT_FOOTER
  } = useGeneralHeights(mainBannerHeight);

  const { tableCircularProgressDiv } = useTableStyles();

  useEffect(() => {
    setParams({ hideFilters });

    setSelectableRows(
      getSelectableRows(
        route,
        tableRowData,
        user,
        isProductionEnv,
        isReleaseFeatureEnabledForUser,
        getAccessStatus(USER_PERMISSIONS.ManageCompanies) // this line disables all checkboxes in companies page if user is not allowed to manage companies data
      )
    );
    if (initialLoading || isDetailsResetNeeded) {
      clearSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hideFilters,
    setSelectableRows,
    tableRowData,
    user,
    initialLoading,
    isDetailsResetNeeded
  ]);

  const isTableEmpty = tableRowData?.length === 0;
  const companiesPage = route === ROUTES.Companies;
  const companiesMediumScreen = companiesPage && toMd;
  const companiesMobileTableHeight =
    companiesMediumScreen && MOBILE_TABLE_HEIGHT_WITHOUT_FOOTER;

  const renderTable = () => {
    if (isDetailsResetNeeded && loading) {
      return <TableLoader route={route} hasDetails={hasDetails} />;
    }
    if (isTableEmpty) {
      // isDetailsResetNeeded check is needed to display loader instead of double empty table render on page change (Dashboard page)
      return loading || initialLoading || isDetailsResetNeeded ? (
        <TableLoader route={route} hasDetails={hasDetails} />
      ) : (
        <TableEmpty route={route} hasSearch={hasSearch} />
      );
    }

    return (
      <div>
        {loading && (
          <div css={css(tableCircularProgressDiv)}>
            <CircularProgress data-testid="table-circular-progress" />
          </div>
        )}
        {toMd ? (
          <MobileTable
            route={route}
            hasDetails={hasDetails}
            toMd={toMd}
            toLg={toLg}
            hasTableView={hasTableView}
            hasTabFilters={hasTabFilters}
            hasSearch={hasSearch}
            tableRowSwitch={tableRowSwitch}
          />
        ) : (
          <DesktopTable
            route={route}
            hasTabFilters={hasTabFilters}
            hasDetails={hasDetails}
            hasSearch={hasSearch}
            hasTableView={hasTableView}
            tableRowSwitch={tableRowSwitch}
          />
        )}
      </div>
    );
  };

  const calculateMobileTableHeight = () => {
    const tableHeight = companiesPage
      ? COMPANIES_SX_MOBILE_TABLE_HEIGHT
      : MOBILE_TABLE_HEIGHT_WITHOUT_FOOTER;

    return hasSearch ? MOBILE_TABLE_HEIGHT : tableHeight;
  };

  return (
    <div
      data-testid="table"
      style={{
        position: "relative",
        height: toSm
          ? calculateMobileTableHeight()
          : companiesMobileTableHeight || DESKTOP_TABLE_HEIGHT,
        width: "100%"
      }}
    >
      {tabFilters}
      <TableToolbar
        route={route}
        toMd={toMd}
        hasSearch={hasSearch}
        hasTableView={hasTableView}
      />
      {renderTable()}

      {hasFooter && <TableFooter route={route} />}
    </div>
  );
};

export default React.memo(Table);
